import * as React from 'react';
import { ConfirmDeleteSingleImage } from './ConfirmDeleteSingleImage';
import { ThumbnailModal } from './ThumbnailModal';
import { useApiUrl } from '../../../../../../shared/hooks/useApiUrl';

// Based of ImageThumbnail: app/features/orders/view/imageprocessing/list/ImageThumbnail.tsx

export type DigitalStylingThumbnailLabels = {
    tooltipDelete: string;
    confirmDeleteSingleTitle: string;
    confirmDeleteSingleText: string;
    confirmDeleteSingleAcceptButton: string;
    confirmDeleteSingleCancelButton: string;
};

type DigitalStylingThumbnailProps = {
    id: number;
    filename: string;
    imageReference: string | null;
    highResolutionImageReference: string | null;
    onClick: (event: React.MouseEvent<HTMLDivElement>, id: number) => void;
    onDelete: (imageId: number, isUploadedByCustomer: boolean) => void;
    labels: DigitalStylingThumbnailLabels;
    isSelected: boolean;
    isUploadedByCustomer: boolean;
};

type ReadyThumbnailProps = {
    id: number;
    filename: string;
    imageReference: string;
    highResolutionImageReference: string;
    onClick?: (event: React.MouseEvent<HTMLDivElement>, id: number) => void;
    onDelete: (imageId: number, isUploadedByCustomer: boolean) => void;
    labels: DigitalStylingThumbnailLabels;
    isSelected: boolean;
    isUploadedByCustomer: boolean;
};

type LoadingThumbnailProps = {
    filename: string;
};

function ReadyThumbnail({
    id,
    filename,
    onClick,
    onDelete,
    imageReference,
    highResolutionImageReference,
    labels,
    isSelected,
    isUploadedByCustomer
}: ReadyThumbnailProps) {
    let apiUrl = useApiUrl();
    let [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    let [showThumbnailModal, setShowThumbnailModal] = React.useState(false);

    function handleCloseConfirmDelete() {
        setShowConfirmDelete(false);
    }

    function handleDeleteSingleImage() {
        setShowConfirmDelete(true);
    }

    function handleAccept() {
        onDelete(id, isUploadedByCustomer);
        setShowConfirmDelete(false);
    }

    function handleClickThumbnail(event: React.MouseEvent<HTMLDivElement>) {
        if (!event.ctrlKey) {
            setShowThumbnailModal(true);
        }

        if (typeof onClick === 'function') {
            onClick(event, id);
        }
    }

    function handleCloseThumbnailModal() {
        setShowThumbnailModal(false);
    }

    return (
        <>
            <div className={`thumbnail thumbnail-custom ${isSelected ? 'selected' : ''}`}>
                <div>
                    {isSelected ? (
                        <div
                            className="selected-action-icon"
                            data-testid="ImageThumbnail__selectedIcon"
                        >
                            {<i className="material-icons image-action-selected">check</i>}
                        </div>
                    ) : null}
                    <div className="action-icons"></div>
                    <div>
                        <div
                            role="button"
                            className="img-button"
                            aria-label={filename}
                            title={filename}
                            onClick={handleClickThumbnail}
                        >
                            <img
                                className="img-responsive"
                                alt="Digitally styled image"
                                draggable={false}
                                src={`${apiUrl}/file/${imageReference}`}
                            />
                            <span className="img-label">
                                <span className="label-truncate">
                                    {filename ? filename.slice(0, -7) : ''}
                                </span>
                                <span>{filename ? filename.slice(-7) : ''}</span>
                            </span>
                        </div>
                        <i
                            className="thumbnail-delete material-icons"
                            onClick={handleDeleteSingleImage}
                            title={labels.tooltipDelete}
                        >
                            delete
                        </i>
                    </div>
                </div>
            </div>
            <ThumbnailModal
                isOpen={showThumbnailModal}
                imgUrl={`${apiUrl}/file/${highResolutionImageReference}`}
                onClose={handleCloseThumbnailModal}
                filename={filename}
                fullScale={true}
            />
            <ConfirmDeleteSingleImage
                show={showConfirmDelete}
                onClose={handleCloseConfirmDelete}
                onAccept={handleAccept}
                labels={{
                    title: labels.confirmDeleteSingleTitle,
                    text: labels.confirmDeleteSingleText,
                    acceptButton: labels.confirmDeleteSingleAcceptButton,
                    cancelButton: labels.confirmDeleteSingleCancelButton
                }}
            />
        </>
    );
}

function LoadingThumbnail({ filename }: LoadingThumbnailProps) {
    return (
        <div className="thumbnail thumbnail-custom">
            <div>
                <div className="action-icons"></div>
                <div>
                    <div className="img-button" style={{ cursor: 'default' }}>
                        <div className="img-responsive-spinner">
                            <span className="spin-container">
                                <i className="load-spin-dark" />
                            </span>
                        </div>
                        <span className="img-label">
                            <span className="label-truncate">
                                {filename && filename.substr(0, filename && filename.length - 7)}
                            </span>
                            <span>{filename && filename.substr(-7)}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function DigitalStylingThumbnail({
    labels,
    id,
    onDelete,
    filename,
    imageReference,
    highResolutionImageReference,
    onClick,
    isSelected,
    isUploadedByCustomer
}: DigitalStylingThumbnailProps) {
    if (imageReference === null) {
        return <LoadingThumbnail filename={filename} />;
    }

    return (
        <ReadyThumbnail
            id={id}
            filename={filename}
            imageReference={imageReference}
            highResolutionImageReference={highResolutionImageReference}
            onClick={onClick}
            onDelete={onDelete}
            labels={labels}
            isSelected={isSelected}
            isUploadedByCustomer={isUploadedByCustomer}
        />
    );
}
